import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";

const CATEGORIES = ["Bedding", "Curtain", "Table Linen", "Placemat", "Throw", "Other"];
const EMPTY = { templateCode: "", templateName: "", category: "Bedding", orientation: "Landscape", widthMM: 420, heightMM: 297, bleedMM: 3 };

export default function Templates() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = async (q = "") => {
    const { data } = await api.get("/templates", { params: { search: q } });
    setTemplates(data);
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { const tm = setTimeout(() => load(search), 300); return () => clearTimeout(tm); }, [search]);

  const handleCreate = async () => {
    if (!form.templateCode || !form.templateName) { toast.error(t("templates.codeNameRequired")); return; }
    try {
      const { data } = await api.post("/templates", { ...form, widthMM: Number(form.widthMM), heightMM: Number(form.heightMM), bleedMM: Number(form.bleedMM), elementConfig: {} });
      toast.success(t("templates.created"));
      setOpen(false);
      setForm(EMPTY);
      navigate(`/templates/${data.id}`);
    } catch (e) {
      toast.error(t("templates.createFailed"));
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-8 py-10" data-testid="templates-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-700 text-slate-900">{t("templates.title")}</h1>
        {isAdmin && (
          <Button className="bhf-btn-primary gap-1.5 border-0 text-white" onClick={() => setOpen(true)} data-testid="add-template-button">
            <Plus className="h-4 w-4" /> {t("templates.add")}
          </Button>
        )}
      </div>
      <div className="mt-5 relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input className="pl-9" placeholder={t("templates.searchPlaceholder")} value={search} onChange={(e) => setSearch(e.target.value)} data-testid="template-search-input" />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {templates.map((tpl) => (
          <div key={tpl.id} className="bhf-card cursor-pointer overflow-hidden transition-shadow hover:shadow-lg" onClick={() => navigate(`/templates/${tpl.id}`)} data-testid={`template-card-${tpl.id}`}>
            <div className="flex items-center justify-center bg-slate-100 p-6" style={{ aspectRatio: tpl.orientation === "Landscape" ? "1.6/1" : "0.7/1" }}>
              <div className="flex h-full w-full items-center justify-center rounded border-2 border-dashed border-slate-300 bg-white text-[10px] text-slate-400">
                {tpl.widthMM} × {tpl.heightMM} mm
              </div>
            </div>
            <div className="p-3.5">
              <div className="flex items-center justify-between">
                <span className="font-mono-bhf text-[11px] text-indigo-600">{tpl.templateCode}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${tpl.status === "Active" ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>{tpl.status === "Active" ? t("products.active") : t("products.inactive")}</span>
              </div>
              <div className="mt-1 truncate text-sm font-semibold text-slate-800">{tpl.templateName}</div>
              <div className="mt-0.5 text-xs text-slate-500">{tpl.category} · {tpl.orientation === "Landscape" ? t("templates.landscape") : t("templates.portrait")} · v{tpl.version}</div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="template-dialog">
          <DialogHeader><DialogTitle>{t("templates.addTitle")}</DialogTitle></DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label>{t("templates.code")}</Label><Input className="mt-1" value={form.templateCode} onChange={(e) => setForm({ ...form, templateCode: e.target.value })} data-testid="new-template-code-input" /></div>
            <div><Label>{t("templates.name")}</Label><Input className="mt-1" value={form.templateName} onChange={(e) => setForm({ ...form, templateName: e.target.value })} data-testid="new-template-name-input" /></div>
            <div>
              <Label>{t("templates.category")}</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("templates.orientation")}</Label>
              <Select value={form.orientation} onValueChange={(v) => setForm({ ...form, orientation: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Landscape">{t("templates.landscape")}</SelectItem><SelectItem value="Portrait">{t("templates.portrait")}</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>{t("templates.width")}</Label><Input type="number" className="mt-1" value={form.widthMM} onChange={(e) => setForm({ ...form, widthMM: e.target.value })} /></div>
            <div><Label>{t("templates.height")}</Label><Input type="number" className="mt-1" value={form.heightMM} onChange={(e) => setForm({ ...form, heightMM: e.target.value })} /></div>
            <div><Label>{t("templates.bleed")}</Label><Input type="number" className="mt-1" value={form.bleedMM} onChange={(e) => setForm({ ...form, bleedMM: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
            <Button className="bhf-btn-primary border-0 text-white" onClick={handleCreate} data-testid="create-template-submit">{t("templates.create")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
